import * as React from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { Clock } from "lucide-react";
import { TimeTrackingSource } from "@/hooks/useTimeTracking";

interface TimeTrackerProps {
  value: number; // time in minutes (current DB value or local state)
  // For DB-first updates: emit increment, parent handles DB-verified calculation
  onAddTime?: (minutesToAdd: number, source: TimeTrackingSource) => void;
  // For local state/form management: emit the new total value directly
  onChange?: (newTotal: number, source: TimeTrackingSource) => void;
  className?: string;
  allowManualInput?: boolean;
}

export const TimeTracker = ({ value, onAddTime, onChange, className, allowManualInput = true }: TimeTrackerProps) => {
  const [manualInput, setManualInput] = React.useState(value.toString());

  // Update manual input when value changes externally (from DB confirmation or parent)
  React.useEffect(() => {
    setManualInput(value.toString());
  }, [value]);

  // Handle time additions - supports both patterns
  const addTime = (minutes: number, source: TimeTrackingSource) => {
    if (onAddTime) {
      // DB-first pattern: emit only the increment
      onAddTime(minutes, source);
    } else if (onChange) {
      // Local state pattern: calculate and emit new total
      onChange(value + minutes, source);
    }
  };

  const handleManualInput = (inputValue: string) => {
    setManualInput(inputValue);
    const numValue = parseInt(inputValue);
    if (!isNaN(numValue) && numValue >= 0) {
      if (onAddTime) {
        // DB-first pattern: calculate difference and emit as increment
        const difference = numValue - value;
        if (difference !== 0) {
          onAddTime(difference, 'manual');
        }
      } else if (onChange) {
        // Local state pattern: emit the new value directly
        onChange(numValue, 'manual');
      }
    }
  };

  const formatTime = (minutes: number) => {
    if (minutes < 60) {
      return `${minutes}min`;
    }
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    if (remainingMinutes === 0) {
      return `${hours}h`;
    }
    return `${hours}h ${remainingMinutes}min`;
  };

  return (
    <div className={cn("flex items-center gap-2", className)}>
      <Clock className="h-4 w-4 text-muted-foreground flex-shrink-0" />
      
      {/* Quick add buttons */}
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => addTime(15, 'quick_add_15')}
        className="h-7 px-2 text-xs"
      >
        +15'
      </Button>
      
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => addTime(30, 'quick_add_30')}
        className="h-7 px-2 text-xs hidden sm:inline-flex"
      >
        +30'
      </Button>
      
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => addTime(60, 'quick_add_60')}
        className="h-7 px-2 text-xs"
      >
        +60'
      </Button>

      {/* Manual input - only show when allowed */}
      {allowManualInput && (
        <>
          <Input
            type="number"
            min="0"
            value={manualInput}
            onChange={(e) => handleManualInput(e.target.value)}
            className="h-7 w-16 text-xs"
            placeholder="0"
          />
          <span className="text-xs text-muted-foreground">min</span>
        </>
      )}
      
      <span className="text-xs text-muted-foreground">
        ({formatTime(value)})
      </span>
    </div>
  );
};